import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import * as notificationService from '../services/notificationService';

// Create the context
const NotificationContext = createContext(null);

/**
 * Provider component for notifications management
 * @param {object} props - Component props
 * @returns {JSX.Element} Notification provider
 */
export const NotificationProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Disable automatic polling
  // const [pollingInterval, setPollingInterval] = useState(null);

  // Function to fetch notifications from the API
  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated() || !user) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { notifications: fetchedNotifications, unreadCount } = 
        await notificationService.getUserNotifications();
      
      setNotifications(fetchedNotifications);
      setUnreadCount(unreadCount);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load notifications');
      // console.error('Error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  // Initialize notifications when user logs in - but only once, no polling
  useEffect(() => {
    if (isAuthenticated() && user) {
      // Load notifications once on login
      fetchNotifications();
      
      // Disable automatic polling to prevent API flooding
      // const interval = setInterval(fetchNotifications, 60000); // Poll every minute
      // setPollingInterval(interval);
      
      // return () => {
      //   if (pollingInterval) {
      //     clearInterval(pollingInterval);
      //   }
      // };
    } else {
      // Clear notifications when user logs out
      setNotifications([]);
      setUnreadCount(0);
      
      // if (pollingInterval) {
      //   clearInterval(pollingInterval);
      //   setPollingInterval(null);
      // }
    }
  }, [isAuthenticated, user, fetchNotifications]);

  /**
   * Mark a notification as read
   * @param {string} notificationId - ID of the notification to mark as read
   */
  const markAsRead = async (notificationId) => {
    try {
      await notificationService.markNotificationAsRead(notificationId);
      
      // Update local state
      setNotifications(prevNotifications => 
        prevNotifications.map(notification => 
          notification.id === notificationId
            ? { ...notification, read: true }
            : notification
        )
      );
      
      // Update unread count
      setUnreadCount(prevCount => Math.max(0, prevCount - 1));
    } catch (err) {
      // console.error('Error marking notification as read:', err);
    }
  };

  /**
   * Mark all notifications as read
   */
  const markAllAsRead = async () => {
    try {
      await notificationService.markAllNotificationsAsRead();
      
      // Update local state
      setNotifications(prevNotifications => 
        prevNotifications.map(notification => ({ ...notification, read: true }))
      );
      
      // Reset unread count
      setUnreadCount(0);
    } catch (err) {
      // console.error('Error marking all notifications as read:', err);
    }
  };

  /**
   * Delete a notification
   * @param {string} notificationId - ID of the notification to delete
   */
  const deleteNotification = async (notificationId) => {
    try {
      await notificationService.deleteNotification(notificationId);
      
      // Update local state
      const notificationToRemove = notifications.find(n => n.id === notificationId);
      setNotifications(prevNotifications => 
        prevNotifications.filter(notification => notification.id !== notificationId)
      );
      
      // Update unread count if needed
      if (notificationToRemove && !notificationToRemove.read) {
        setUnreadCount(prevCount => Math.max(0, prevCount - 1));
      }
    } catch (err) {
      // console.error('Error deleting notification:', err);
    }
  };

  /**
   * Add a new local notification (for system messages not from the server)
   * @param {object} notification - Notification object to add
   */
  const addLocalNotification = (notification) => {
    const newNotification = {
      id: `local-${Date.now()}`,
      timestamp: new Date(),
      read: false,
      ...notification
    };
    
    setNotifications(prev => [newNotification, ...prev]);
    setUnreadCount(prev => prev + 1);
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        loading,
        error,
        fetchNotifications,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        addLocalNotification
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

/**
 * Hook to use the notification context
 * @returns {object} Notification context
 */
export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}; 